import * as revenuesModuleFunctions from "./revenues";
import * as expensesModuleFunctions from "./expenses";
import * as payroll from "./payroll";
import { GET, POST, PATCH, DELETE, POST_FORM } from "../config";

/**
 * =================================
 * MÉTODOS DE PAGO (Payment Methods)
 * =================================
 */

/**
 * Listar métodos de pago
 */
export const listPaymentMethods = async ({
  branch = null,
  is_active = null,
} = {}) => {
  const params = new URLSearchParams();

  // Solo agregar branch si se proporciona explícitamente
  // El UniversalFilterMixin ya filtra por sucursales del usuario automáticamente
  if (branch) params.append("branch", branch);
  if (is_active !== null) params.append("is_active", is_active);

  // Si no hay parámetros, hacer la petición sin query params para que el backend use el filtro automático
  const url = params.toString()
    ? `finance/payment-methods/?${params.toString()}`
    : `finance/payment-methods/`;

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener métodos de pago activos
 */
export const getActivePaymentMethods = async (branch = null) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const response = await GET(
    `finance/payment-methods/active/?${params.toString()}`
  );
  return response.data;
};

/**
 * Crear método de pago
 */
export const createPaymentMethod = async (data) => {
  const response = await POST("finance/payment-methods/", data);
  return response.data;
};

/**
 * Actualizar método de pago
 */
export const updatePaymentMethod = async (id, data) => {
  const response = await PATCH(`finance/payment-methods/${id}/`, data);
  return response.data;
};

/**
 * Eliminar método de pago
 */
export const deletePaymentMethod = async (id) => {
  const response = await DELETE(`finance/payment-methods/${id}/`);
  return response.data;
};

/**
 * =================================
 * PAGOS (Payments)
 * =================================
 */

/**
 * Listar pagos
 */
export const listPayments = async (params = {}) => {
  const {
    page = 1,
    page_size = 20,
    ...rest
  } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("page", page);
  queryParams.append("page_size", page_size);

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      queryParams.append(key, value);
    }
  });

  const response = await GET(`finance/payments/?${queryParams.toString()}`);
  return response.data;
};

export const list = listPayments; // Alias

/**
 * Obtener un pago específico
 */
export const getPayment = async (id) => {
  const response = await GET(`finance/payments/${id}/`);
  return response.data;
};

/**
 * Crear un pago
 */
export const createPayment = async (data) => {
  const response = await POST("finance/payments/", data);
  return response.data;
};

export const create = createPayment; // Alias

/**
 * Actualizar un pago
 */
export const updatePayment = async (id, data) => {
  const response = await PATCH(`finance/payments/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un pago
 */
export const deletePayment = async (id) => {
  const response = await DELETE(`finance/payments/${id}/`);
  return response.data;
};

/**
 * Obtener resumen de pagos
 * Acepta tanto 'branch' como 'branch_id' para compatibilidad con el backend
 */
export const getPaymentsSummary = async (params = {}) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      // Priorizar branch_id sobre branch si ambos vienen, pero Object.entries enviará ambos si existen
      queryParams.append(key === "branch_id" ? "branch" : key, value);
    }
  });

  const response = await GET(`finance/payments/summary/?${queryParams.toString()}`);
  return response.data;
};

export const getSummary = getPaymentsSummary; // Alias

/**
 * Procesar un pago (marcar como completado)
 */
export const processPayment = async (id, data = {}) => {
  const response = await POST(`finance/payments/${id}/process/`, data);
  return response.data;
};

/**
 * Cancelar un pago (rechazar)
 * Usa el endpoint específico de cancel que sincroniza automáticamente
 * el estado del ingreso/egreso asociado
 */
export const cancelPayment = async (id, data = {}) => {
  const response = await POST(`finance/payments/${id}/cancel/`, {
    reason: data.reason || data.notes || "Pago cancelado",
  });
  return response.data;
};

/**
 * Recuperar un pago cancelado (volver a PENDING)
 */
export const recoverPayment = async (id, data) => {
  const response = await PATCH(`finance/payments/${id}/`, {
    status: "PENDING",
    notes: data.notes || "Pago recuperado",
  });
  return response.data;
};

/**
 * Reembolsar un pago
 */
export const refundPayment = async (id, data) => {
  const response = await POST(`finance/payments/${id}/refund/`, data);
  return response.data;
};

/**
 * Obtener estadísticas de pagos
 * 
 * NOTA: changePaymentDirection fue removido - el payment_direction se determina
 * automáticamente según las relaciones (Revenue/Order = INCOME, FixedExpense = EXPENSE)
 */
export const getPaymentStats = async ({
  branch = null,
  period = "month",
} = {}) => {
  const params = new URLSearchParams();

  if (branch) params.append("branch", branch);
  if (period) params.append("period", period);

  const response = await GET(`finance/payments/stats/?${params.toString()}`);
  return response.data;
};

/**
 * Exportar pagos a Excel
 */
export const exportToExcel = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined) {
      queryParams.append(key, params[key]);
    }
  });

  // Usar GET con responseType blob para descargar archivos
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  const response = await Axios.get(
    `finance/payments/export-excel/?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: "blob",
    }
  );
  return response;
};

/**
 * Descargar comprobante de pago con diseño completo formato 80mm
 * Usa payment_pdf_generator con QR, código de barras, etc.
 * Si hay archivo adjunto, lo descarga. Si no, genera PDF automáticamente.
 * GET /api/finance/payments/{id}/download-voucher/
 */
export const downloadPaymentVoucher = async (id, format = "thermal") => {
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  try {
    const params = new URLSearchParams();
    if (format) {
      // Usar pdf_format en lugar de format para evitar conflictos con DRF
      params.append("pdf_format", format);
    }
    // Intentar primero con download-voucher, si falla usar generate-pdf (legacy)
    const url = params.toString()
      ? `finance/payments/${id}/download-voucher/?${params.toString()}`
      : `finance/payments/${id}/download-voucher/`;

    // Si download-voucher no funciona, usar generate-pdf como fallback
    const fallbackUrl = params.toString()
      ? `finance/payments/${id}/generate-pdf/?${params.toString()}`
      : `finance/payments/${id}/generate-pdf/`;
    let response;
    try {
      // Intentar primero con download-voucher
      response = await Axios.get(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
        responseType: "blob",
      });
    } catch (error) {
      // Si falla con 404, intentar con generate-pdf (legacy)
      if (error.response?.status === 404) {
        console.warn(
          "download-voucher no encontrado, usando generate-pdf (legacy)"
        );
        response = await Axios.get(fallbackUrl, {
          headers: {
            Authorization: `Token ${token}`,
          },
          responseType: "blob",
        });
      } else {
        throw error;
      }
    }

    // Verificar si la respuesta es realmente un PDF o un error JSON
    // Los errores del backend con responseType: 'blob' vienen como JSON dentro de un Blob
    if (response.data instanceof Blob) {
      // Verificar el Content-Type de la respuesta
      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        // Es un error JSON, leerlo como texto
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(
          errorData.error ||
            errorData.detail ||
            "Error al descargar comprobante"
        );
      }
      // Es un PDF válido
      return response.data;
    }

    return response.data;
  } catch (error) {
    // Si es un error de axios con respuesta blob que es JSON
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(
          errorData.error ||
            errorData.detail ||
            "Error al descargar comprobante"
        );
      } catch (parseError) {
        // Si no se puede parsear, usar el mensaje del error original
        const errorMsg = error.message || "Error al descargar comprobante";
        throw new Error(errorMsg);
      }
    }
    // Si el error tiene un mensaje, usarlo
    if (error.message) {
      throw error;
    }
    throw new Error("Error al descargar comprobante");
  }
};

/**
 * Generar PDF de comprobante de pago (legacy - usar downloadPaymentVoucher)
 * @deprecated Usar downloadPaymentVoucher en su lugar
 */
export const generatePaymentPdf = async (paymentId) => {
  // Redirigir a downloadPaymentVoucher para mantener compatibilidad
  return downloadPaymentVoucher(paymentId);
};

/**
 * =================================
 * CONFIGURACIÓN FINANCIERA POR SUCURSAL
 * =================================
 */
export const branchConfigs = {
  list: async (params) => {
    const response = await GET("/finance/branch-configs/", { params });
    return response.data;
  },
  get: async (id) => {
    const response = await GET(`/finance/branch-configs/${id}/`);
    return response.data;
  },
  getByBranch: async (branchId) => {
    const response = await GET(`/finance/branch-configs/by_branch/${branchId}/`);
    return response.data;
  },
  formatOnly: async (branchId) => {
    const response = await GET("/finance/branch-configs/format_only/", { params: { branch: branchId } });
    return response.data;
  },
  formatPreview: async (id) => {
    const response = await GET(`/finance/branch-configs/${id}/format_preview/`);
    return response.data;
  },
  create: async (data) => {
    const response = await POST("/finance/branch-configs/", data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await PATCH(`/finance/branch-configs/${id}/`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await DELETE(`/finance/branch-configs/${id}/`);
    return response.data;
  },
};

/**
 * =================================
 * MÉTODOS DE PAGO (Wrapper para objeto exportado)
 * =================================
 */
export const payment_methods = {
  list: listPaymentMethods,
  getActive: getActivePaymentMethods,
  create: createPaymentMethod,
  update: updatePaymentMethod,
  delete: deletePaymentMethod,
};

/**
 * =================================
 * PAGOS UNIFICADOS (Wrapper para objeto exportado)
 * =================================
 */
export const payments = {
  list: listPayments,
  get: getPayment,
  create: createPayment,
  update: updatePayment,
  delete: deletePayment,
  getSummary: getPaymentsSummary,
  process: processPayment,
  cancel: cancelPayment,
  recover: recoverPayment,
  refund: refundPayment,
  // changeDirection: REMOVED - el payment_direction se determina automáticamente
  getStats: getPaymentStats,
  exportToExcel: exportToExcel,
  downloadVoucher: downloadPaymentVoucher,
  generatePdf: generatePaymentPdf, // Legacy - usar downloadVoucher
};

/**
 * =================================
 * INGRESOS (REVENUES)
 * =================================
 */
export const revenueCategories = {
  list: revenuesModuleFunctions.listRevenueCategories,
  create: revenuesModuleFunctions.createRevenueCategory,
  update: revenuesModuleFunctions.updateRevenueCategory,
  delete: revenuesModuleFunctions.deleteRevenueCategory,
};

export const revenuesModule = {
  list: revenuesModuleFunctions.listRevenues,
  get: revenuesModuleFunctions.getRevenue,
  create: revenuesModuleFunctions.createRevenue,
  update: revenuesModuleFunctions.updateRevenue,
  delete: revenuesModuleFunctions.deleteRevenue,
  getSummary: revenuesModuleFunctions.getRevenuesSummary,
  getPending: revenuesModuleFunctions.getPendingRevenues,
  getPendingByClient: revenuesModuleFunctions.getPendingRevenuesByClient,
  getReceived: revenuesModuleFunctions.getReceivedRevenues,
  markAsReceived: revenuesModuleFunctions.markRevenueAsReceived,
  markAsUnpaid: revenuesModuleFunctions.markRevenueAsUnpaid,
  updateStatus: revenuesModuleFunctions.updateRevenueStatus,
  addPayment: revenuesModuleFunctions.addRevenuePayment,
  getPayments: revenuesModuleFunctions.getRevenuePayments,
  convertToExpense: revenuesModuleFunctions.convertRevenueToExpense,
  generateBoletaPdf: revenuesModuleFunctions.generateBoletaPdf,
  downloadVoucher: revenuesModuleFunctions.downloadRevenueVoucher,
  exportToExcel: revenuesModuleFunctions.exportToExcel,
  cancelRevenue: revenuesModuleFunctions.cancelRevenue,
};

// Alias para compatibilidad: api.finance.revenues también funciona
export const revenues = revenuesModule;

/**
 * =================================
 * GASTOS (EXPENSES)
 * =================================
 */
export const expenseCategories = {
  list: expensesModuleFunctions.listExpenseCategories,
  create: expensesModuleFunctions.createExpenseCategory,
  update: expensesModuleFunctions.updateExpenseCategory,
  delete: expensesModuleFunctions.deleteExpenseCategory,
};

export const expensesModule = {
  list: expensesModuleFunctions.listFixedExpenses,
  get: expensesModuleFunctions.getFixedExpense,
  create: expensesModuleFunctions.createFixedExpense,
  update: expensesModuleFunctions.updateFixedExpense,
  delete: expensesModuleFunctions.deleteFixedExpense,
  getActive: expensesModuleFunctions.getActiveExpenses,
  getSummary: expensesModuleFunctions.getExpensesSummary,
  updateStatus: expensesModuleFunctions.updateExpenseStatus,
  getByFrequency: expensesModuleFunctions.getExpensesByFrequency,
  addPayment: expensesModuleFunctions.addExpensePayment,
  getPayments: expensesModuleFunctions.getExpensePayments,
  convertToRevenue: expensesModuleFunctions.convertExpenseToRevenue,
  downloadVoucher: expensesModuleFunctions.downloadExpenseVoucher,
  exportToExcel: expensesModuleFunctions.exportToExcel,
  cancelExpense: expensesModuleFunctions.cancelExpense,
};

// Alias para compatibilidad: api.finance.expenses también funciona
export const expenses = expensesModule;

/**
 * =================================
 * NÓMINA (PAYROLL)
 * =================================
 */
export const payrollModule = {
  periods: {
    list: payroll.listPeriods,
    get: payroll.getPeriod,
    create: payroll.createPeriod,
    update: payroll.updatePeriod,
    partialUpdate: payroll.partialUpdatePeriod,
    delete: payroll.deletePeriod,
    calculatePayroll: payroll.calculatePayroll,
    processAllPayments: payroll.processAllPayments,
    getSummary: payroll.getPeriodSummary,
  },
  entries: {
    list: payroll.listEntries,
    get: payroll.getEntry,
    create: payroll.createEntry,
    update: payroll.updateEntry,
    partialUpdate: payroll.partialUpdateEntry,
    delete: payroll.deleteEntry,
    applyDeductions: payroll.applyDeductions,
    applyBonuses: payroll.applyBonuses,
    processPayment: payroll.processPayment,
  },
  payments: {
    list: payroll.listPayments,
    get: payroll.getPayment,
  },
};

/**
 * =================================
 * FOLIOS SII (CAF)
 * =================================
 */
const FOLIOS_ENDPOINT = "folios/";

export const folios = {
  /**
   * Listar folios del usuario
   * @param {Object} params - { branch, document_type, is_active }
   */
  list: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.branch) queryParams.append("branch", params.branch);
    if (params.document_type) queryParams.append("document_type", params.document_type);
    if (params.is_active !== undefined) queryParams.append("is_active", params.is_active);
    
    const query = queryParams.toString();
    const response = await GET(`${FOLIOS_ENDPOINT}${query ? `?${query}` : ""}`);
    return response.data;
  },

  /**
   * Obtener detalle de un folio
   * @param {number} id - ID del folio
   */
  get: async (id) => {
    const response = await GET(`${FOLIOS_ENDPOINT}${id}/`);
    return response.data;
  },

  /**
   * Subir nuevo archivo CAF
   * @param {Object} data - { branch, document_type, caf_file, alert_at, is_active }
   */
  create: async (data) => {
    const response = await POST_FORM(FOLIOS_ENDPOINT, data);
    return response.data;
  },

  /**
   * Actualizar folio
   * @param {number} id - ID del folio
   * @param {Object} data - Datos a actualizar
   */
  update: async (id, data) => {
    const response = await PATCH(`${FOLIOS_ENDPOINT}${id}/`, data);
    return response.data;
  },

  /**
   * Eliminar folio (solo si no tiene uso)
   * @param {number} id - ID del folio
   */
  delete: async (id) => {
    const response = await DELETE(`${FOLIOS_ENDPOINT}${id}/`);
    return response.data;
  },

  /**
   * Obtener folios disponibles por sucursal y tipo
   * @param {Object} params - { branch (requerido), document_type }
   */
  getAvailable: async (params) => {
    const queryParams = new URLSearchParams();
    if (params.branch) queryParams.append("branch", params.branch);
    if (params.document_type) queryParams.append("document_type", params.document_type);
    
    const response = await GET(`${FOLIOS_ENDPOINT}available/?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Verificar alertas de stock bajo
   */
  checkAlerts: async () => {
    const response = await GET(`${FOLIOS_ENDPOINT}check_alerts/`);
    return response.data;
  },

  /**
   * Obtener resumen de folios por sucursal
   * @param {number} branchId - ID de la sucursal
   */
  getSummary: async (branchId) => {
    const response = await GET(`${FOLIOS_ENDPOINT}summary/?branch=${branchId}`);
    return response.data;
  },

  /**
   * Activar/desactivar un rango de folios
   * @param {number} id - ID del folio
   */
  toggleActive: async (id) => {
    const response = await POST(`${FOLIOS_ENDPOINT}${id}/toggle_active/`, {});
    return response.data;
  },

  /**
   * Actualizar umbral de alerta
   * @param {number} id - ID del folio
   * @param {number} alertAt - Nuevo umbral
   */
  updateAlert: async (id, alertAt) => {
    const response = await PATCH(`${FOLIOS_ENDPOINT}${id}/update_alert/`, { alert_at: alertAt });
    return response.data;
  },
};

/**
 * =================================
 * TIPOS DE DOCUMENTO SII
 * =================================
 */
export const DOCUMENT_TYPES = {
  BOLETA: "39",
  FACTURA: "33",
  NOTA_CREDITO: "61",
  NOTA_DEBITO: "56",
  GUIA_DESPACHO: "52",
};

export const DOCUMENT_TYPE_LABELS = {
  "39": "Boleta Electrónica",
  "33": "Factura Electrónica",
  "61": "Nota de Crédito",
  "56": "Nota de Débito",
  "52": "Guía de Despacho",
};

/**
 * =================================
 * TIPOS DE MÉTODOS DE PAGO (COPIADO DE finance_payments)
 * =================================
 */
export const PAYMENT_TYPES = {
  CASH: "Efectivo",
  BANK_TRANSFER: "Transferencia Bancaria",
  CHECK: "Cheque",
  CREDIT_CARD: "Tarjeta de Crédito",
  DEBIT_CARD: "Tarjeta de Débito",
  DIGITAL_WALLET: "Billetera Digital",
  CRYPTO: "Criptomoneda",
  OTHER: "Otro",
};

/**
 * =================================
 * ESTADOS DE PAGO (COPIADO DE finance_payments)
 * =================================
 */
export const PAYMENT_STATUSES = {
  PENDING: "Pendiente",
  PROCESSING: "Procesando",
  COMPLETED: "Completado",
  FAILED: "Fallido",
  REFUNDED: "Reembolsado",
  CANCELLED: "Cancelado",
};

/**
 * =================================
 * OTRAS CONSTANTES
 * =================================
 */
export const REVENUE_TYPES = revenuesModuleFunctions.REVENUE_TYPES;
export const REVENUE_STATUSES = revenuesModuleFunctions.REVENUE_STATUSES;
export const REVENUE_CATEGORY_TYPES = revenuesModuleFunctions.REVENUE_CATEGORY_TYPES;
export const EXPENSE_CATEGORY_TYPES = expensesModuleFunctions.EXPENSE_CATEGORY_TYPES;
export const EXPENSE_FREQUENCIES = expensesModuleFunctions.EXPENSE_FREQUENCIES;
export const EXPENSE_STATUSES = expensesModuleFunctions.EXPENSE_STATUSES;
